import React, { useEffect, useState } from 'react';
import { LinkedInPreview } from './previews/LinkedInPreview';
import { InstagramPreview } from './previews/InstagramPreview';
import { FacebookPreview } from './previews/FacebookPreview';
import { TwitterPreview } from './previews/TwitterPreview';
import { YouTubePreview } from './previews/YouTubePreview';
import { GoogleBusinessPreview } from './previews/GoogleBusinessPreview';
import { TikTokPreview } from './previews/TitkokPreview';
import { PinterestPreview } from './previews/PinterestPreview';
import type { SocialPlatform } from '../icons/SocialIcon';
import type { Brand } from '../../types';
import type { Post } from '../../types/calendar';
import type { PlatformContent } from '../../services/contentService';

interface SocialMediaPostPreviewProps {
  platform: SocialPlatform;
  content: string | PlatformContent | object;
  brand?: Brand;
  brandName?: string;
  brandLogo?: string;
  media?: Post['media'];
  mediaUrl?: string; // explicit URL from upstream (e.g., freshly uploaded File object URL)
  mediaUrls?: string[]; // multi-images
  format?: 'regular' | 'reel';
  hideEngagementCounts?: boolean;
}

export function SocialMediaPostPreview({
  platform,
  content,
  brand,
  brandName,
  brandLogo,
  media,
  mediaUrl: mediaUrlProp,
  mediaUrls,
  format = 'regular',
  hideEngagementCounts
}: SocialMediaPostPreviewProps) {
  const [resolvedMediaUrl, setResolvedMediaUrl] = useState<string | undefined>(undefined);

  // Resolve a usable media URL:
  // - Use direct URL if present
  // - Otherwise, if media is a File/Blob (freshly uploaded), create an object URL
  // - If media is an array, resolve from the first entry
  useEffect(() => {
    let objectUrlToRevoke: string | undefined;

    const getDirectUrl = (m: any): string | undefined => {
      if (!m) return undefined;
      if (typeof m === 'string') return m || undefined;
      if (typeof m === 'object' && typeof (m as any).url === 'string') return (m as any).url || undefined;
      return undefined;
    };

    const isFileOrBlob = (v: any): v is File | Blob => {
      // Guard for environments without DOM File/Blob
      return typeof window !== 'undefined' && (v instanceof File || v instanceof Blob);
    };

    const getFileLike = (m: any): File | Blob | undefined => {
      if (!m) return undefined;
      if (isFileOrBlob(m)) return m;
      if (Array.isArray(m)) {
        const first = m[0];
        if (isFileOrBlob(first)) return first;
        if (first && typeof first === 'object' && isFileOrBlob((first as any).file)) return (first as any).file;
        return undefined;
      }
      if (typeof m === 'object' && m !== null) {
        if (isFileOrBlob((m as any).file)) return (m as any).file;
        if (isFileOrBlob((m as any).blob)) return (m as any).blob;
      }
      return undefined;
    };

    // 1) Highest priority: explicit URL prop from upstream
    if (typeof mediaUrlProp === 'string' && mediaUrlProp.trim() !== '') {
      setResolvedMediaUrl(mediaUrlProp);
      return () => {};
    }

    // 2) Direct URL on media object
    const directUrl = getDirectUrl(media);
    if (directUrl && directUrl.trim() !== '') {
      setResolvedMediaUrl(directUrl);
      return () => {};
    }

    const fileLike = getFileLike(media);
    if (fileLike) {
      objectUrlToRevoke = URL.createObjectURL(fileLike);
      setResolvedMediaUrl(objectUrlToRevoke);
    } else {
      setResolvedMediaUrl(undefined);
    }

    return () => {
      if (objectUrlToRevoke) {
        URL.revokeObjectURL(objectUrlToRevoke);
      }
    };
  }, [media, mediaUrlProp]);
  
  // Use either brand object or individual props
  const displayBrandName = brandName || brand?.name;
  const displayBrandLogo = brandLogo || brand?.logo;
  
  // Ensure content is a string
  let contentString = '';
  
  if (typeof content === 'string') {
    // Preserve newlines in string content and handle escaped newlines
    contentString = content.replace(/\\n/g, '\n'); // Convert escaped newlines to real newlines
  } else if (content && typeof content === 'object') {
    // Check if it has a text property (PlatformContent)
    if ('text' in content && content.text) {
      contentString = typeof content.text === 'string' 
        ? content.text.replace(/\\n/g, '\n') // Convert escaped newlines in content.text
        : JSON.stringify(content.text);
    } else {
      // Otherwise stringify the whole object
      contentString = JSON.stringify(content);
    }
  } else {
    contentString = String(content);
  }

  // Make sure contentString maintains any newline characters

  // Split platform previews into a separate function for clarity
  return renderPlatformPreview(platform, contentString, brand, displayBrandName, displayBrandLogo, resolvedMediaUrl, format, mediaUrls, hideEngagementCounts);
}

// Helper function to render the appropriate platform preview
function renderPlatformPreview(
  platform: SocialPlatform, 
  content: string, 
  brand?: Brand, 
  brandName?: string, 
  brandLogo?: string, 
  mediaUrl?: string, 
  format: 'regular' | 'reel' = 'regular',
  mediaUrls?: string[],
  hideEngagementCounts?: boolean
) {
  // Process the content one more time to ensure we replace any escaped newlines
  const processedContent = content.replace(/\\n/g, '\n');
  
  // Check platform and render appropriate preview
  switch (platform) {
    case 'linkedin':
      return <LinkedInPreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} mediaUrls={mediaUrls} format={format} hideEngagementCounts={hideEngagementCounts} />;
      
    case 'instagram':
      return <InstagramPreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} mediaUrls={mediaUrls} format={format} hideEngagementCounts={hideEngagementCounts} />;
      
    case 'facebook':
      return <FacebookPreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} mediaUrls={mediaUrls} format={format} hideEngagementCounts={hideEngagementCounts} />;
      
    case 'twitter':
      return <TwitterPreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} format={format} />;
      
    case 'youtube':
      return <YouTubePreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} format={format} />;
      
    case 'google-business':
      return <GoogleBusinessPreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} format={format} />;
      
    case 'tiktok':
      return <TikTokPreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} format={format} />;
      
    case 'pinterest':
      return <PinterestPreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} mediaUrls={mediaUrls} format={format} hideEngagementCounts={hideEngagementCounts} platformData={content} />;
      
    default:
      return <LinkedInPreview content={processedContent} brand={brand} brandName={brandName} brandLogo={brandLogo} mediaUrl={mediaUrl} mediaUrls={mediaUrls} format={format} />;
  }
}

// Helper to get content string for each platform
export const getContentString = (
  content: string | { platforms: Record<string, string | PlatformContent> } | any,
  platform: string
): string => {
  if (typeof content === 'string') {
    return content;
  }
  
  if (content && typeof content === 'object' && 'platforms' in content) {
    const platformContent = content.platforms[platform];
    
    if (typeof platformContent === 'string') {
      return platformContent;
    }
    
    if (platformContent && typeof platformContent === 'object' && 'text' in platformContent) {
      return typeof platformContent.text === 'string' 
        ? platformContent.text 
        : JSON.stringify(platformContent.text);
    }
    
    return JSON.stringify(platformContent);
  }
  
  return typeof content === 'object' ? JSON.stringify(content) : String(content);
};

export default SocialMediaPostPreview;